import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useApi } from '../contexts/ApiContext';
import Button from '../components/ui/Button';

const DashboardPage: React.FC = () => {
    const [userEmail, setUserEmail] = useState('');
    const [loginTime, setLoginTime] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [isPersonType, setIsPersonType] = useState(false);
    const [isPrimaryContact, setIsPrimaryContact] = useState(false);
    const [selectedCustomerCode, setSelectedCustomerCode] = useState('');
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { showAlert } = useAlert();
    const { allRequirements, loadingRequirements, fetchRequirements, clearCache, customers, fetchCustomers } = useApi();

    useEffect(() => {
        // Get user info from auth context and session storage
        const email = user?.email || sessionStorage.getItem('profileEmail') || sessionStorage.getItem('userEmail') || sessionStorage.getItem('email');
        const accessToken = sessionStorage.getItem('accessToken');

        if (email) {
            setUserEmail(email);
            setLoginTime(new Date().toLocaleString());
            if (accessToken) {
                setSessionId(accessToken.substring(0, 20) + '...');
            }
        }

        // Check if user is a company primary contact
        const customerListStr = sessionStorage.getItem('customerList');
        if (customerListStr) {
            try {
                const customerList = JSON.parse(customerListStr);
                // Check if user is a company primary contact for their specific customer code
                const customerCode = sessionStorage.getItem('customerCode');
                const matchedCustomer = customerList?.find(
                    (customer: any) => customer.customer_code === customerCode
                );
                const hasPrimaryContact = matchedCustomer?.company_primary_contact === true;
                setIsPrimaryContact(hasPrimaryContact);
                const hasPersonType = !!matchedCustomer?.person_type?.trim();
                setIsPersonType(hasPersonType);
            } catch (error) {
                console.error('Error parsing customer list:', error);
            }
        }

        // Set the active customer code for the org switcher
        const currentCode = sessionStorage.getItem('customerCode') || '';
        setSelectedCustomerCode(currentCode);

        // Load customers for org switcher and pending requirements
        fetchCustomers();
        fetchRequirements();
    }, [user, fetchRequirements, fetchCustomers]);

    const handleLogout = () => {
        // Clear all session storage
        sessionStorage.clear();

        // Call logout from auth context if available
        if (logout) {
            logout();
        }

        clearCache();

        showAlert('Logged out successfully!', 'success');
        setTimeout(() => navigate('/'), 1000);
    };

    const showProfile = () => {
        navigate('/profile', { state: { from: 'dashboard' } });
    };

    const showRequirements = () => {
        navigate('/requirements');
    };

    const showCompany = () => {
        navigate('/company-menu');
    };

    const handleRequirementClick = (requirement: any) => {
        navigate('/requirement-detail', { state: { requirement } });
    };

    const handleOrgSwitch = async (newCustomerCode: string) => {
        if (!customers || newCustomerCode === selectedCustomerCode) return;

        const customer = customers.find(c => c.customer_code === newCustomerCode);
        if (!customer) return;

        // Update session storage with the newly selected organisation
        sessionStorage.setItem('customerCode', customer.customer_code);
        sessionStorage.setItem('customerName', customer.customer_name);
        sessionStorage.setItem('personType', customer.person_type);
        sessionStorage.setItem('peopleId', customer.people_id);
        sessionStorage.setItem('companyId', customer.company_id);
        sessionStorage.setItem('customerList', JSON.stringify([customer]));

        // Re-derive role flags for the new organisation
        setSelectedCustomerCode(newCustomerCode);
        setIsPrimaryContact((customer as any).company_primary_contact === true);
        setIsPersonType(!!customer.person_type?.trim());

        // Clear cache and reload requirements for the new organisation
        clearCache();
        await fetchRequirements(true);
    };

    const handleFeatureClick = (featureName: string) => {
        showAlert(`${featureName} feature coming soon!`, 'info');
    };

    return (
        <div className="container">
            <div className="header">
                <h1>🎉 Welcome!</h1>
                <p>You're successfully logged in</p>
            </div>

            <div className="dashboard">
                {/* Organisation switcher – only shown when the user belongs to more than one org */}
                {customers && customers.length > 1 && (
                    <div style={{
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}>
                        <label htmlFor="orgSwitch" style={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', margin: 0 }}>
                            🏢 :
                        </label>
                        <select
                            id="orgSwitch"
                            value={selectedCustomerCode}
                            onChange={(e) => handleOrgSwitch(e.target.value)}
                            style={{
                                flex: 1,
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                fontSize: '15px',
                                backgroundColor: '#efe9e9',
                                cursor: 'pointer',
                            }}
                        >
                            {customers.map((customer) => (
                                <option key={customer.customer_code} value={customer.customer_code}>
                                    {customer.customer_name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {isPersonType && loadingRequirements ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                        <p>Loading requirements...</p>
                    </div>
                ) : isPersonType ? (
                    <>
                        {allRequirements && allRequirements.pendingRequirements && allRequirements.pendingRequirements.length > 0 && (
                            <div className="pending-requirements">
                                <h3>Your {allRequirements.pendingRequirements.length} Pending Requirements</h3>
                                <div className="requirements-list">
                                    {allRequirements.pendingRequirements.map((doc) => (
                                        <div key={doc.requirement_id} className="requirement-item">
                                            <div className="requirement-info">
                                                <h4>{doc.requirement_name}</h4>
                                                {/*<p><strong>Type:</strong> {doc.type}</p>*/}
                                                <p><span className={`status-${doc.signed.toString().toLowerCase()}`}>{doc.signed}</span></p>
                                                {/*{doc.dueDate && <p><strong>Due Date:</strong> {new Date(doc.dueDate).toLocaleDateString()}</p>}*/}
                                            </div>
                                            <div className="requirement-actions">
                                                <button
                                                    className="action-btn-small"
                                                    onClick={() => handleRequirementClick(doc)}
                                                >
                                                    Action
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {allRequirements && allRequirements.expiredRequirements && allRequirements.expiredRequirements.length > 0 && (
                            <div className="expired-requirements">
                                <h3>Your {allRequirements.expiredRequirements.length} Expired Requirements</h3>
                                <div className="requirements-list">
                                    {allRequirements.expiredRequirements.map((doc) => (
                                        <div key={doc.requirement_id} className="requirement-item">
                                            <div className="requirement-info">
                                                <h4>{doc.requirement_name}</h4>
                                                {/*<p><strong>Type:</strong> {doc.type}</p>*/}
                                                <p><span className={`status-${doc.signed.toString().toLowerCase()}`}>{doc.signed}</span></p>
                                                {/*{doc.dueDate && <p><strong>Due Date:</strong> {new Date(doc.dueDate).toLocaleDateString()}</p>}*/}
                                            </div>
                                            <div className="requirement-actions">
                                                <button
                                                    className="action-btn-small"
                                                    onClick={() => handleRequirementClick(doc)}
                                                >
                                                    Action
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {allRequirements && allRequirements.rejectedRequirements && allRequirements.rejectedRequirements.length > 0 && (
                            <div className="rejected-requirements">
                                <h3>{allRequirements.rejectedRequirements.length} Rejected Requirements</h3>
                                <div className="requirements-list">
                                    {allRequirements.rejectedRequirements.map((doc) => (
                                        <div key={doc.requirement_id} className="requirement-item">
                                            <div className="requirement-info">
                                                <h4>{doc.requirement_name}</h4>
                                                {/*<p><strong>Type:</strong> {doc.type}</p>*/}
                                                <p><span className={`status-${doc.signed.toString().toLowerCase()}`}>{doc.signed}</span></p>
                                                {/*{doc.dueDate && <p><strong>Due Date:</strong> {new Date(doc.dueDate).toLocaleDateString()}</p>}*/}
                                                {doc.status_comment && (
                                                    <p style={{ color: '#c0392b', fontSize: '13px', marginTop: '4px' }}>
                                                        <strong>Rejection reason:</strong> {doc.status_comment}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="requirement-actions">
                                                <button
                                                    className="action-btn-small"
                                                    onClick={() => handleRequirementClick(doc)}
                                                >
                                                    Action
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(!allRequirements ||
                            (!allRequirements.pendingRequirements?.length &&
                             !allRequirements.expiredRequirements?.length &&
                             !allRequirements.rejectedRequirements?.length)) && (
                            <div className="no-requirements">
                                <p>✅ No pending personal requirements at this time.</p>
                            </div>
                        )}
                    </>
                ) : null}

                {isPrimaryContact && loadingRequirements ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                        <p>Loading company requirements...</p>
                    </div>
                ) : isPrimaryContact ? (
                    <>
                        {allRequirements && allRequirements.pendingCompanyRequirements && allRequirements.pendingCompanyRequirements.length > 0 && (
                            <div className="pending-requirements">
                                <h3>{allRequirements.pendingCompanyRequirements.length} Pending Company Requirements</h3>
                                <div className="requirements-list">
                                    {allRequirements.pendingCompanyRequirements.map((doc) => (
                                        <div key={doc.requirement_id} className="requirement-item">
                                            <div className="requirement-info">
                                                <h4>{doc.requirement_name}</h4>
                                                {/*<p><strong>Type:</strong> {doc.type}</p>*/}
                                                <p><span className={`status-${doc.signed.toString().toLowerCase()}`}>{doc.signed}</span></p>
                                                {/*{doc.dueDate && <p><strong>Due Date:</strong> {new Date(doc.dueDate).toLocaleDateString()}</p>}*/}
                                            </div>
                                            <div className="requirement-actions">
                                                <button
                                                    className="action-btn-small"
                                                    onClick={() => handleRequirementClick(doc)}
                                                >
                                                    Action
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {allRequirements && allRequirements.expiredCompanyRequirements && allRequirements.expiredCompanyRequirements.length > 0 && (
                            <div className="expired-requirements">
                                <h3>{allRequirements.expiredCompanyRequirements.length} Expired Company Requirements</h3>
                                <div className="requirements-list">
                                    {allRequirements.expiredCompanyRequirements.map((doc) => (
                                        <div key={doc.requirement_id} className="requirement-item">
                                            <div className="requirement-info">
                                                <h4>{doc.requirement_name}</h4>
                                                {/*<p><strong>Type:</strong> {doc.type}</p>*/}
                                                <p><span className={`status-${doc.signed.toString().toLowerCase()}`}>{doc.signed}</span></p>
                                                {/*{doc.dueDate && <p><strong>Due Date:</strong> {new Date(doc.dueDate).toLocaleDateString()}</p>}*/}
                                            </div>
                                            <div className="requirement-actions">
                                                <button
                                                    className="action-btn-small"
                                                    onClick={() => handleRequirementClick(doc)}
                                                >
                                                    Action
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {allRequirements && allRequirements.rejectedCompanyRequirements && allRequirements.rejectedCompanyRequirements.length > 0 && (
                            <div className="rejected-requirements">
                                <h3>{allRequirements.rejectedCompanyRequirements.length} Rejected Company Requirements</h3>
                                <div className="requirements-list">
                                    {allRequirements.rejectedCompanyRequirements.map((doc) => (
                                        <div key={doc.requirement_id} className="requirement-item">
                                            <div className="requirement-info">
                                                <h4>{doc.requirement_name}</h4>
                                                {/*<p><strong>Type:</strong> {doc.type}</p>*/}
                                                <p><span className={`status-${doc.signed.toString().toLowerCase()}`}>{doc.signed}</span></p>
                                                {/*{doc.dueDate && <p><strong>Due Date:</strong> {new Date(doc.dueDate).toLocaleDateString()}</p>}*/}
                                                {doc.status_comment && (
                                                    <p style={{ color: '#c0392b', fontSize: '13px', marginTop: '4px' }}>
                                                        <strong>Rejection reason:</strong> {doc.status_comment}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="requirement-actions">
                                                <button
                                                    className="action-btn-small"
                                                    onClick={() => handleRequirementClick(doc)}
                                                >
                                                    Action
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(!allRequirements ||
                            (!allRequirements.pendingCompanyRequirements?.length &&
                             !allRequirements.expiredCompanyRequirements?.length &&
                             !allRequirements.rejectedCompanyRequirements?.length)) && (
                            <div className="no-requirements">
                                <p>✅ No pending company requirements at this time.</p>
                            </div>
                        )}
                    </>
                ) : null}

                <div className="action-buttons">
                    {isPersonType && (
                        <button className="action-btn" onClick={showProfile}>
                            👤 View Profile
                        </button>
                    )}
                    {isPrimaryContact && (
                        <button className="action-btn" onClick={showCompany}>
                            🏢 Company
                        </button>
                    )}
                    {isPersonType && (
                        <button className="action-btn" onClick={showRequirements}>
                            🗒️ Requirements
                        </button>
                    )}
                    {/*<button className="action-btn" onClick={() => handleFeatureClick('Help & Support')}>*/}
                    {/*    ❓ Help & Support*/}
                    {/*</button>*/}
                    <Button variant="secondary" onClick={handleLogout}>
                        🚪 Logout
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;